import React, { useEffect, useState } from "react";
import { api } from "./api";
import { eastern } from "./RecordScreen";

/**
 * The season (build plan §11.1 #5), System Admin only: End the session,
 * Archive (every List, the documents and the index copied to _Archive/<year>/,
 * then every copy read back and compared), and, on its own page, Clear —
 * refused without a verified archive for exactly that year, and in the live
 * app until 30 days after the session was ended.
 */

interface Session { start: string; end: string; year: string; ended: boolean; endedBy: "date" | "action" | null; endedUtc: string; endedByEmail: string }
interface Archive { year: string; folder: string; archivedUtc: string; byEmail: string; verified: boolean; verifiedUtc: string; counts: Record<string, number>; documents: number; problems: string[]; cleared?: { clearedUtc: string; byEmail: string; removed: Record<string, number> } }
interface View { session: Session; archive: Archive | null; archives: Record<string, Archive> }

function useSeason(): [View | null, (v: View) => void, string | null] {
  const [v, setV] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ season: View }>("GET", "/api/season").then((r) => (r.ok ? setV(r.data.season) : setError(r.data.error ?? "Could not load.")));
  }, []);
  return [v, setV, error];
}

function SessionLine({ s }: { s: Session }) {
  return <p className="text-sm">{s.ended ? (s.endedBy === "action" ? `The session was ended on ${eastern(s.endedUtc)} by ${s.endedByEmail}.` : `The session ended on ${s.end}.`) : `The session (${s.start || "?"} to ${s.end || "?"}) hasn't ended yet.`}</p>;
}

function ArchiveTable({ a }: { a: Archive }) {
  return (
    <div className="space-y-2">
      <p className={`text-sm font-semibold ${a.verified ? "text-emerald-800" : "text-red-700"}`} role="status">{a.verified ? `Verified ${eastern(a.verifiedUtc)}: every copy read back and matched.` : "NOT verified: see the problems below. Archive again."}</p>
      <p className="text-sm text-slate-600">Archived {eastern(a.archivedUtc)} by {a.byEmail} to <code className="break-all">{a.folder}</code>. {a.documents} document{a.documents === 1 ? "" : "s"} copied.</p>
      <div className="overflow-x-auto">
        <table className="text-left text-sm">
          <thead className="text-slate-500"><tr><th className="pr-4 font-medium">List</th><th className="font-medium">Rows archived</th></tr></thead>
          <tbody>{Object.entries(a.counts).map(([k, n]) => <tr key={k}><td className="pr-4">{k}</td><td>{n}</td></tr>)}</tbody>
        </table>
      </div>
      {a.problems.length > 0 && <ul className="list-disc pl-5 text-sm text-red-700">{a.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      {a.cleared && <p className="text-sm">Cleared {eastern(a.cleared.clearedUtc)} by {a.cleared.byEmail}.</p>}
    </div>
  );
}

export function Season(): React.ReactElement {
  const [v, setV, loadError] = useSeason();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function run(what: "end" | "archive") {
    setBusy(what);
    setError(null);
    const r = await api<{ season: View }>("POST", `/api/season/${what}`, {});
    setBusy(null);
    if (!r.ok) return setError(r.data.error ?? "That didn't work.");
    setV(r.data.season);
  }
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/config" className="text-program underline">← Config</a></p>
      <h2 className="mb-1 text-xl font-semibold">The season</h2>
      <p className="mb-3 text-sm text-slate-600">After the session: end it, archive it (a permanent, verified copy), and only then clear it for next year.</p>
      {(loadError || error) && <p className="field-error" role="alert">{loadError || error}</p>}
      {v && (
        <>
          <div className="section-card space-y-2">
            <h3 className="text-base font-semibold">1. End the session</h3>
            <SessionLine s={v.session} />
            <p className="text-sm text-slate-600">Ending it lets the end-of-week reports be drafted for everyone and the season be archived. Everything else works before and after.</p>
            {v.session.endedBy !== "action" && <button type="button" className="btn-secondary min-h-11" disabled={busy !== null} onClick={() => void run("end")}>End the session</button>}
          </div>
          <div className="section-card space-y-2">
            <h3 className="text-base font-semibold">2. Archive the {v.session.year} season</h3>
            <p className="text-sm text-slate-600">Copies all 12 Lists (JSON and CSV), the documents folder and the document index to <code>_Archive/{v.session.year}/</code>, then reads every copy back and compares it. Nothing is deleted.</p>
            {v.archive ? <ArchiveTable a={v.archive} /> : <p className="text-sm">Not archived yet.</p>}
            {(!v.archive || !v.archive.verified) && <button type="button" className="btn-primary min-h-11" disabled={!v.session.ended || busy !== null} onClick={() => void run("archive")}>{busy === "archive" ? "Archiving…" : "Archive the season"}</button>}
          </div>
          <div className="section-card space-y-2">
            <h3 className="text-base font-semibold">3. Clear the season</h3>
            <p className="text-sm text-slate-600">A separate step, on its own page, once the archive is verified.</p>
            <a className="btn-secondary inline-flex min-h-11 items-center" href="/season/clear">Clear the season…</a>
          </div>
        </>
      )}
    </section>
  );
}

export function SeasonClear(): React.ReactElement {
  const [v, setV, loadError] = useSeason();
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ removed: Record<string, number>; files: number } | null>(null);
  async function clear() {
    setBusy(true);
    setError(null);
    const r = await api<{ result: { removed: Record<string, number>; files: number }; season: View }>("POST", "/api/season/clear", { year });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not cleared.");
    setDone(r.data.result);
    setV(r.data.season);
  }
  const a = v?.archive;
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/season" className="text-program underline">← The season</a></p>
      <h2 className="mb-1 text-xl font-semibold">Clear the season</h2>
      {(loadError || error) && <p className="field-error" role="alert">{loadError || error}</p>}
      {v && (
        <div className="section-card space-y-3">
          <p className="text-sm">This removes the {v.session.year} season's rows from the eight record Lists (roster, notes, medications, administrations, visits, notifications, documents, audit) and the season's documents. Users, Staff, Supplies (with the counts started again) and Config stay. It can't be undone: the archive is the record from now on.</p>
          {a?.verified && !a.cleared ? <ArchiveTable a={a} /> : <p className="text-sm font-semibold text-red-700">{a?.cleared ? "This season was already cleared." : `There is no verified archive for ${v.session.year}: archive the season first.`}</p>}
          {done ? (
            <p className="text-sm font-semibold text-emerald-800" role="status">Cleared: {Object.entries(done.removed).map(([k, n]) => `${k} ${n}`).join(", ")}; {done.files} document{done.files === 1 ? "" : "s"}.</p>
          ) : (
            a?.verified && !a.cleared && (
              <>
                <div className="max-w-48">
                  <label htmlFor="sc-year" className="field-label">Type the season's year ({v.session.year}) to confirm</label>
                  <input id="sc-year" inputMode="numeric" className="field-input" value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} autoComplete="off" />
                </div>
                <button type="button" className="btn-primary min-h-11 bg-red-700" disabled={busy || year !== v.session.year} onClick={() => void clear()}>{busy ? "Clearing…" : `Clear the ${v.session.year} season`}</button>
              </>
            )
          )}
        </div>
      )}
    </section>
  );
}
